import React, { useEffect, useMemo, useState } from 'react';
import { trimmedText, useRecordChanges, type RecordComparators } from '../hooks/useRecordChanges';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import { Plus, Tag as TagIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Tag } from '../types/api';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '../contexts/UserContext';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { usePermissions } from '../hooks/usePermissions';
import { apiService } from '../services/api';
import { classifyError, humanizeError, tagParentRejection, type TagParentRejection } from '../lib/errors';
import { useResource } from '../hooks/useResource';
import { EMPTY_VALUE } from '../lib/formatters';
import { batchFetcher } from '../lib/listPage';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  DataTable,
  DataTableColumn,
  SearchInput,
  hasListError,
  isListRetrying,
  listTableProps,
} from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { ListEmptyState } from '../components/ListEmptyState';
import { ErrorState } from '../components/ErrorState';
import { FormError } from '../components/FormError';
import { Field } from '../components/Field';
import { InfiniteCombobox } from '../components/InfiniteCombobox';
import { ListFilter } from '../components/ListFilter';
import { PageHeader } from '../components/PageHeader';
import { DialogActions, DialogForm } from '../components/DialogActions';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { PropertyList, PropertyRow } from '../components/PropertyList';
import { TAG_TYPE_LABELS, formatEnum } from '../lib/labels';
import { OrganizationCell } from '../components/OrganizationCell';
import { DateCell } from '../components/Timestamp';
import { routeCopy } from '../routes';
import {
  TAG_SLUG_HELP,
  TAG_SLUG_MAX_LENGTH,
  resolvedTagSlug,
  validateTagDraft,
  type TagFieldErrors,
} from '../lib/tagForm';

/** h1 and description, from the route registry (DESIGN_SYSTEM.md §6 Naming). */
const PAGE = routeCopy('/tags');

/**
 * The Parent column sorts by `parentName` — the value it renders — never by
 * the parent's UUID, which grouped rows in an order nobody could read.
 */
type SortBy = 'id' | 'name' | 'slug' | 'description' | 'parentName' | 'tagType' | 'createdAt';

const SORTABLE_KEYS: ReadonlyArray<SortBy> = [
  'id',
  'name',
  'slug',
  'description',
  'parentName',
  'tagType',
  'createdAt',
];

const DEFAULT_TAG_TYPES = ['object'];

type TagScope = 'org' | 'all';

const SCOPE_OPTIONS: ReadonlyArray<{ value: TagScope; label: string }> = [
  { value: 'org', label: 'This organization' },
  { value: 'all', label: 'All organizations' },
];

/** A parent refusal, in the words that explain it — shown under Parent event type. */
const PARENT_REJECTION_COPY: Record<TagParentRejection, string> = {
  cycle: "The event type you picked sits under this one, so it can't be its parent.",
  self: "An event type can't be its own parent.",
  not_found: 'That parent event type no longer exists.',
};

const NO_PARENT = '';

const parentLabel = (tag: Pick<Tag, 'name' | 'slug'>) => `${tag.name} (${tag.slug})`;

interface ParentEventTypeFieldProps {
  organizationId: string;
  accessToken: string;
  value: string;
  onChange: (parentId: string, parent: Tag | null) => void;
  /** Edit only: the edited event type, whose whole subtree is left out. */
  excludeSubtreeOf?: string;
  id?: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/** An event type's form, as the inputs hold it. */
interface TagFormValues {
  name: string;
  slug: string;
  description: string;
  parentId: string;
  tagType: string;
}

/** What Create event type opens with — its discard guard's baseline. */
const CREATE_TAG_DEFAULTS: TagFormValues = {
  name: '',
  slug: '',
  description: '',
  parentId: '',
  tagType: 'object',
};

const TAG_COMPARATORS: RecordComparators<TagFormValues> = {
  name: trimmedText,
  slug: trimmedText,
  description: trimmedText,
};

/**
 * The parent picker: a server-searched combobox over this organization's event
 * types (ruling 21). Options load only while it is open, so every open is fresh
 * — an event type created a moment ago is offered with no reload. On Edit the
 * server leaves out the event type and everything beneath it, so a loop is
 * never offered. The current parent, when it sits outside the loaded batches,
 * is fetched by id and pinned first.
 */
const ParentEventTypeField: React.FC<ParentEventTypeFieldProps> = ({
  organizationId,
  accessToken,
  value,
  onChange,
  excludeSubtreeOf,
  id,
  disabled,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}) => {
  const [open, setOpen] = useState(false);

  const list = useInfiniteList<Tag, 'name'>({
    ...batchFetcher<Tag, 'name'>(async ({ limit, offset, cursor, search, signal }) => {
      const response = await apiService.getTags(organizationId, accessToken, {
        limit,
        offset,
        cursor,
        search,
        sortBy: 'name',
        sortDirection: 'asc',
        excludeSubtreeOf,
        signal,
      });
      return { items: response.tags, pagination: response.pagination };
    }),
    rowKey: (tag) => tag.id,
    enabled: open && Boolean(organizationId && accessToken),
    errorContext: "Couldn't load parent event types",
  });

  const selectedResource = useResource(
    async (signal) => {
      const response = await apiService.getTags(organizationId, accessToken, {
        limit: 1,
        offset: 0,
        search: '',
        sortBy: 'name',
        sortDirection: 'asc',
        ids: [value],
        signal,
      });
      return response.tags[0] ?? null;
    },
    { enabled: Boolean(value && organizationId && accessToken), deps: [value, organizationId, accessToken] },
  );

  return (
    <InfiniteCombobox<Tag>
      list={list}
      open={open}
      onOpenChange={setOpen}
      rowKey={(tag) => tag.id}
      optionLabel={parentLabel}
      value={value}
      onValueChange={onChange}
      leadingItems={[{ value: NO_PARENT, label: 'None' }]}
      selectedRow={selectedResource.data ?? null}
      placeholder="None"
      searchPlaceholder="Search event types…"
      itemNoun="event types"
      errorTitle="Couldn't load event types"
      id={id}
      disabled={disabled}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      triggerClassName="w-full"
    />
  );
};

/**
 * One event type, created or edited in one dialog (DESIGN_SYSTEM.md §9 "One
 * field, one control"; §6 cites MyLenses as the pattern). One form state and
 * one errors object; whether `tag` is set decides the title, the description,
 * the submit label, Delete event type and the parent picker's excluded subtree.
 * A reader — or any row seen under 'All organizations' — gets values.
 */
interface TagDialogProps {
  open: boolean;
  /** null + open = create. */
  tag: Tag | null;
  readOnly: boolean;
  orgId: string;
  accessToken: string;
  tagTypes: string[];
  /** The category option load's failure, shown under the fields. */
  optionLoadErrors: React.ReactNode;
  canDelete: boolean;
  onClose: () => void;
  onCreated: (created: Tag, parent: Tag | null) => void;
  onSaved: (updated: Tag, previous: Tag) => void;
  onDeleteRequest: (tag: Tag) => void;
}

const tagFormOf = (tag: Tag | null): TagFormValues =>
  tag
    ? {
      name: tag.name,
      slug: tag.slug,
      description: tag.description || '',
      parentId: tag.parentId || '',
      tagType: tag.tagType || 'object',
    }
    : CREATE_TAG_DEFAULTS;

const TagDialog: React.FC<TagDialogProps> = ({
  open, tag, readOnly, orgId, accessToken, tagTypes, optionLoadErrors, canDelete,
  onClose, onCreated, onSaved, onDeleteRequest,
}) => {
  const isEdit = Boolean(tag);
  const [form, setForm] = useState<TagFormValues>(CREATE_TAG_DEFAULTS);
  /** What the dialog opened with. State set on open, never a memo over the row. */
  const [baseline, setBaseline] = useState<TagFormValues | null>(null);
  const [errors, setErrors] = useState<TagFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  /** The parent row picked: POST's answer carries no parent labels. */
  const [pickedParent, setPickedParent] = useState<Tag | null>(null);

  const tagId = tag?.id ?? null;
  useEffect(() => {
    if (!open) return;
    const opened = tagFormOf(tag);
    setForm(opened);
    setBaseline(opened);
    setErrors({});
    setSubmitError(null);
    setPickedParent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tagId]);

  const changes = useRecordChanges(baseline, form, TAG_COMPARATORS);
  const guard = useDiscardGuard({
    isDirty: !readOnly && changes.isDirty,
    isBusy: isSaving,
    onClose,
    recordNoun: tag ? `event type "${tag.name}"` : 'the new event type',
  });

  /** A field's error clears as soon as that field changes (DESIGN_SYSTEM §5 Validation). */
  const updateField = (key: keyof TagFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key as keyof TagFieldErrors] ? { ...prev, [key]: undefined } : prev));
  };

  const typeOptions = useMemo(
    () => (form.tagType && !tagTypes.includes(form.tagType) ? [form.tagType, ...tagTypes] : tagTypes),
    [form.tagType, tagTypes],
  );

  const reportFailure = (err: unknown, context: string) => {
    // A refused parent belongs under Parent event type; a taken slug under
    // Slug; anything else above the footer.
    const rejection = tagParentRejection(err);
    const failure = classifyError(err);
    if (rejection) {
      setErrors((prev) => ({ ...prev, parentId: PARENT_REJECTION_COPY[rejection] }));
    } else if (failure.kind === 'conflict' && failure.field === 'slug') {
      setErrors((prev) => ({ ...prev, slug: humanizeError(err) }));
    } else {
      setSubmitError(humanizeError(err, context));
    }
  };

  const submit = async () => {
    const found = validateTagDraft(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSaving(true);
    setSubmitError(null);
    try {
      if (tag) {
        /*
         * Only what changed: PUT /tags/{id} validates and writes only the
         * fields present, so the whole form would quietly undo a concurrent
         * edit to a field nobody here touched.
         */
        const changed = changes.changes;
        const payload: Parameters<typeof apiService.updateTag>[2] = {};
        if (changed.name !== undefined) payload.name = form.name.trim();
        if (changed.slug !== undefined) payload.slug = form.slug.trim();
        if (changed.description !== undefined) payload.description = form.description.trim() || null;
        if (changed.parentId !== undefined) payload.parentId = form.parentId.trim() || null;
        if (changed.tagType !== undefined) payload.tagType = form.tagType.trim() || 'object';
        if (Object.keys(payload).length === 0) return;
        const updated = await apiService.updateTag(tag.id, orgId, payload, accessToken);
        toast.success('Event type updated');
        onSaved(updated, tag);
      } else {
        const created = await apiService.createTag(
          orgId,
          {
            name: form.name.trim(),
            slug: form.slug.trim(),
            description: form.description.trim() || null,
            parentId: form.parentId.trim() || null,
            tagType: form.tagType.trim() || 'object',
          },
          accessToken,
        );
        toast.success('Event type created');
        const parent = form.parentId && pickedParent?.id === form.parentId ? pickedParent : null;
        onCreated(created, parent);
      }
    } catch (err: unknown) {
      console.error(tag ? 'Failed to update tag:' : 'Failed to create tag:', err);
      reportFailure(err, tag ? "Couldn't update the event type" : "Couldn't create the event type");
    } finally {
      setIsSaving(false);
    }
  };

  const storedSlug = resolvedTagSlug(form.slug);

  return (
    <>
      <Dialog open={open} pending={isSaving} onOpenChange={guard.onOpenChange}>
        <DialogContent size="md">
          <DialogHeader>
            {/* Named from the saved record, never the live form, so the title
                holds still while someone types. */}
            <DialogTitle>
              {tag ? (readOnly ? tag.name : `Edit event type "${tag.name}"`) : 'Create event type'}
            </DialogTitle>
            <DialogDescription>
              {tag
                ? readOnly
                  ? 'Event type details.'
                  : 'Change how this event type is named, categorised and nested.'
                : 'Add an event type to this organization.'}
            </DialogDescription>
          </DialogHeader>

          {tag && readOnly ? (
            <>
              <DialogBody>
                <PropertyList>
                  <PropertyRow label="Name">{tag.name}</PropertyRow>
                  <PropertyRow label="Slug" mono>{tag.slug}</PropertyRow>
                  <PropertyRow label="Category">{formatEnum(TAG_TYPE_LABELS, tag.tagType)}</PropertyRow>
                  <PropertyRow label="Parent event type">
                    {tag.parentName
                      ? `${tag.parentName} (${tag.parentSlug || EMPTY_VALUE})`
                      : tag.parentId || null}
                  </PropertyRow>
                  <PropertyRow label="Description">
                    {tag.description ? <span className="whitespace-pre-wrap">{tag.description}</span> : null}
                  </PropertyRow>
                </PropertyList>
              </DialogBody>
              <DialogActions>
                <Button type="button" variant="outline" onClick={onClose}>
                  Close
                </Button>
              </DialogActions>
            </>
          ) : (
            <DialogForm
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              <DialogBody className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name" error={errors.name} required>
                    {(control) => (
                      <Input
                        {...control}
                        value={form.name}
                        onChange={(event) => updateField('name', event.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label="Slug"
                    error={errors.slug}
                    required
                    help={
                      storedSlug ? (
                        <>
                          Saved as <span className="font-mono text-foreground">{storedSlug}</span>
                        </>
                      ) : (
                        TAG_SLUG_HELP
                      )
                    }
                  >
                    {(control) => (
                      <Input
                        {...control}
                        mono
                        value={form.slug}
                        maxLength={TAG_SLUG_MAX_LENGTH + 20}
                        onChange={(event) => updateField('slug', event.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Category">
                    {(control) => (
                      <Select value={form.tagType} onValueChange={(value) => updateField('tagType', value)}>
                        <SelectTrigger {...control}>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {typeOptions.map((type) => (
                            <SelectItem key={type} value={type}>
                              {formatEnum(TAG_TYPE_LABELS, type)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  <Field label="Parent event type" error={errors.parentId}>
                    {(control) => (
                      <ParentEventTypeField
                        organizationId={orgId}
                        accessToken={accessToken}
                        excludeSubtreeOf={tag?.id}
                        id={control.id}
                        aria-invalid={control['aria-invalid']}
                        aria-describedby={control['aria-describedby']}
                        value={form.parentId}
                        onChange={(parentId, parent) => {
                          setForm((prev) => ({ ...prev, parentId }));
                          setPickedParent(parent);
                          setErrors((prev) => ({ ...prev, parentId: undefined }));
                        }}
                      />
                    )}
                  </Field>
                </div>

                {optionLoadErrors}

                <Field label="Description">
                  {(control) => (
                    <Textarea
                      {...control}
                      rows={3}
                      value={form.description}
                      onChange={(event) => updateField('description', event.target.value)}
                    />
                  )}
                </Field>

                {/* Destroying the record lives in the body, below the fields it
                    destroys, never in the Save footer (DESIGN_SYSTEM §5). */}
                {tag && canDelete ? (
                  <div className="flex justify-start border-t pt-4">
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => guard.confirmDiscard(() => onDeleteRequest(tag))}
                      disabled={isSaving}
                    >
                      <Trash2 aria-hidden="true" />
                      Delete event type
                    </Button>
                  </div>
                ) : null}
              </DialogBody>
              <FormError message={submitError} />
              <DialogActions>
                <Button type="button" variant="outline" onClick={guard.requestClose} disabled={isSaving}>
                  Cancel
                </Button>
                {/* Edit wakes on a difference; Create follows validity. */}
                <Button type="submit" loading={isSaving} disabled={isEdit && !changes.isDirty}>
                  {isEdit ? 'Save changes' : 'Create event type'}
                </Button>
              </DialogActions>
            </DialogForm>
          )}
        </DialogContent>
      </Dialog>
      {guard.guardDialog}
    </>
  );
};

const Tags: React.FC = () => {
  const { selectedOrganization, isLoading: orgLoading } = useOrganization();
  const { accessToken } = useUser();
  const { hasPermission } = usePermissions();

  const canWrite = hasPermission('tags:write');
  // DELETE /tags/{id} checks tags:delete, a separate grant from :write.
  const canDelete = hasPermission('tags:delete');

  const orgId = selectedOrganization?.id ?? null;

  /*
   * The scope is a filter, not a second reset key (ruling 3): switching between
   * this organization's event types and every organization's reloads from the
   * top and keeps the search. Only an organization switch restores defaults.
   */
  const tagsQuery = useInfiniteList<Tag, SortBy>({
    ...batchFetcher<Tag, SortBy>(async ({ limit, offset, cursor, search, sort, filters, signal }) => {
      const options = {
        limit,
        offset,
        cursor,
        search,
        sortBy: sort?.column ?? ('createdAt' as SortBy),
        sortDirection: sort?.direction ?? ('desc' as const),
        signal,
      };
      const response = filters.scope === 'all'
        ? await apiService.getAllTags(accessToken as string, orgId as string, options)
        : await apiService.getTags(orgId as string, accessToken as string, options);
      return { items: response.tags, pagination: response.pagination };
    }),
    rowKey: (tag) => tag.id,
    // An organization is required in both scopes: without one the
    // all-organizations fetcher has nothing to ask with, and would report a
    // confident "no event types anywhere".
    enabled: Boolean(accessToken && orgId),
    resetKey: orgId,
    initialSort: { column: 'createdAt', direction: 'desc' },
    sortColumns: SORTABLE_KEYS,
    filters: { scope: { default: 'org', allowed: ['org', 'all'] } },
    urlState: true,
    errorContext: "Couldn't load event types",
  });
  const scope = tagsQuery.filters.scope as TagScope;
  const allScope = scope === 'all';

  // The category options: a bounded enum, so one resource. A failure keeps
  // the defaults usable and says so under the field.
  const tagTypesResource = useResource(
    () => apiService.getTagTypes(accessToken ?? '', orgId ?? ''),
    { enabled: Boolean(accessToken && orgId), resetKey: orgId ?? null },
  );
  const tagTypes =
    tagTypesResource.data && tagTypesResource.data.length > 0 ? tagTypesResource.data : DEFAULT_TAG_TYPES;

  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  /*
   * The one expression that decides whether the open event type is a form or
   * a set of values: a reader without tags:write, or any row seen through the
   * all-organizations scope (another organization's event types are never
   * editable here). Permissions subtract inside the same dialog.
   */
  const tagReadOnly = !canWrite || allScope;
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  /** The option load behind the category field: shown under it, never as an empty list. */
  const optionLoadErrors = tagTypesResource.status === 'error' ? (
    <ErrorState
      compact
      title="Couldn't load categories"
      error={tagTypesResource.error}
      onRetry={() => void tagTypesResource.refetch()}
    />
  ) : null;

  const openEditModal = (tag: Tag) => setSelectedTag(tag);
  const openCreateModal = () => setIsCreateModalOpen(true);

  /** Loaded rows whose parent is `parentId` get `patch` (no mark: they were not edited). */
  const patchChildren = (parentId: string, patch: Partial<Tag>) => {
    tagsQuery.items
      .filter((row) => row.parentId === parentId)
      .forEach((row) => tagsQuery.patchItem(row.id, (current) => ({ ...current, ...patch })));
  };

  const removeTag = async () => {
    if (!selectedTag || !selectedOrganization?.id || !accessToken) {
      return;
    }

    try {
      await apiService.deleteTag(selectedTag.id, selectedOrganization.id, accessToken);
    } catch (err: unknown) {
      console.error('Failed to delete tag:', err);
      // Rethrow so ConfirmDialog shows the humanized error inline and stays open.
      throw err;
    }

    toast.success('Event type deleted');
    // The row goes; its children move to the top level, as tags.parent_id is
    // ON DELETE SET NULL. Nothing refetches.
    tagsQuery.removeItem(selectedTag.id);
    patchChildren(selectedTag.id, { parentId: null, parentName: null, parentSlug: null });
    setSelectedTag(null);
  };

  const columns: Array<DataTableColumn<Tag>> = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      className: 'font-medium',
      render: (tag) => tag.name,
    },
    // Ownership only where rows can belong to more than one organization: the
    // "All organizations" scope (DESIGN_SYSTEM.md §6.1 Ownership). Never the id.
    ...(tagsQuery.filters.scope === 'all'
      ? [{
          key: 'organization',
          header: 'Organization',
          // GET /tags/all names each row's owner (ruling 24); a deleted
          // organization reads EMPTY_VALUE, never its id.
          render: (tag: Tag) => <OrganizationCell name={tag.organizationName} slug={tag.organizationSlug} />,
        }]
      : []),
    {
      key: 'slug',
      header: 'Slug',
      sortable: true,
      className: 'font-mono text-xs text-muted-foreground',
      render: (tag) => tag.slug,
    },
    {
      key: 'tagType',
      header: 'Category',
      sortable: true,
      render: (tag) => formatEnum(TAG_TYPE_LABELS, tag.tagType),
    },
    {
      key: 'parentName',
      header: 'Parent event type',
      sortable: true,
      render: (tag) =>
        tag.parentName
          ? `${tag.parentName} (${tag.parentSlug || EMPTY_VALUE})`
          : tag.parentId || EMPTY_VALUE,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (tag) => <DateCell value={tag.createdAt} />,
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      className: 'max-w-[360px] truncate text-muted-foreground',
      render: (tag) => tag.description || EMPTY_VALUE,
    },
  ];

  const emptyState = (
    <ListEmptyState
      icon={TagIcon}
      noun="event types"
      search={tagsQuery.search}
      onClearSearch={tagsQuery.clearSearch}
      filterActive={tagsQuery.hasActiveFilters}
      onClearFilters={tagsQuery.clearFilters}
      filtered={{
        title: 'No event types in any organization',
        description:
          'Event types are created inside a specific organization, so nothing can be created from this view.',
        action: (
          <Button variant="outline" onClick={() => tagsQuery.setFilter('scope', 'org')}>
            Show this organization&apos;s event types
          </Button>
        ),
      }}
      firstRun={{
        title: 'No event types yet',
        description:
          'Create an event type so templates can declare it and subscriptions can request it.',
        action: canWrite ? (
          <Button onClick={openCreateModal}>
            <Plus aria-hidden="true" />
            Create event type
          </Button>
        ) : undefined,
      }}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="shrink-0">
        <PageHeader
          title={PAGE.title}
          description={PAGE.description}
          actions={
            !allScope && canWrite && selectedOrganization ? (
              <Button onClick={openCreateModal}>
                <Plus aria-hidden="true" />
                Create event type
              </Button>
            ) : undefined
          }
        />
      </div>

      {!selectedOrganization && !orgLoading ? (
        <EmptyState
          icon={TagIcon}
          title="No organization selected"
          description="Select an organization from the switcher above to see its event types."
        />
      ) : (
        <DataTable
          {...listTableProps(tagsQuery)}
          listState={orgLoading ? 'initial' : tagsQuery.status}
          columns={columns}
          rowKey={(tag) => tag.id}
          listLabel="Event types"
          itemNoun="event types"
          searchable
          filters={
            <ListFilter<TagScope>
              label="Event type scope"
              value={scope}
              onValueChange={(next) => tagsQuery.setFilter('scope', next)}
              options={SCOPE_OPTIONS}
            />
          }
          search={
            <SearchInput
              value={tagsQuery.searchInput}
              onChange={tagsQuery.setSearchInput}
              noun="event types"
              placeholder="Search by name, slug, or parent…"
              shortcut
            />
          }
          errorState={
            hasListError(tagsQuery) ? (
              <ErrorState
                title="Couldn't load event types"
                error={tagsQuery.error}
                onRetry={tagsQuery.reset}
                isRetrying={isListRetrying(tagsQuery)}
                action={tagsQuery.search ? (
                  <Button variant="outline" size="sm" onClick={tagsQuery.clearSearch}>Clear search</Button>
                ) : undefined}
              />
            ) : undefined
          }
          emptyState={emptyState}
          // Opens in both scopes, on read: the dialog is read-only for a reader
          // and under 'All organizations' (tagReadOnly).
          onOpenRow={openEditModal}
          openLabel={(tag) => tag.name}
        />
      )}

      {orgId && accessToken ? (
        <TagDialog
          open={isCreateModalOpen || Boolean(selectedTag)}
          tag={isCreateModalOpen ? null : selectedTag}
          readOnly={Boolean(selectedTag) && !isCreateModalOpen && tagReadOnly}
          orgId={orgId}
          accessToken={accessToken}
          tagTypes={tagTypes}
          optionLoadErrors={optionLoadErrors}
          canDelete={canDelete}
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedTag(null);
          }}
          onCreated={(created, parent) => {
            setIsCreateModalOpen(false);
            // POST's RETURNING leaves the parent labels out, so they come from
            // the option the user picked. Pinned first, marked New (ruling 20).
            tagsQuery.prependItem(
              {
                ...created,
                parentName: created.parentName ?? parent?.name ?? null,
                parentSlug: created.parentSlug ?? parent?.slug ?? null,
              },
              { mark: 'new' },
            );
          }}
          onSaved={(updated, previous) => {
            setSelectedTag(null);
            // Patched in place from the PUT's answer (it carries the parent
            // labels), marked Updated; a rename reaches the rows that name it
            // as their parent.
            tagsQuery.patchItem(updated.id, () => updated, { mark: 'updated' });
            if (updated.name !== previous.name || updated.slug !== previous.slug) {
              patchChildren(updated.id, { parentName: updated.name, parentSlug: updated.slug });
            }
          }}
          onDeleteRequest={() => setIsDeleteOpen(true)}
        />
      ) : null}

      <ConfirmDialog
        open={isDeleteOpen}
        title={`Delete event type "${selectedTag?.name ?? ''}"?`}
        // activity_tags.parent_id is ON DELETE SET NULL (schema.ts).
        description="Its child event types move to the top level. This can't be undone."
        confirmLabel="Delete event type"
        errorContext="Couldn't delete the event type"
        variant="destructive"
        onConfirm={removeTag}
        onClose={() => setIsDeleteOpen(false)}
      />
    </div>
  );
};

export default Tags;
